/**
 * PromoBanner — Phase 121
 * -----------------------
 * Globaler Kampagnen-Hinweis oberhalb der Navigation. Wird aus site_settings
 * `promo_banner` gelesen. Fällt beim ersten Deployment (noch keine Settings
 * geschrieben) auf sinnvolle Defaults zurück. Dismissible via sessionStorage
 * (nicht localStorage — pro Tab-Session, damit User später wieder erinnert
 * werden). Wird NICHT im /admin und nicht im /fahrer angezeigt.
 *
 * Alle Style-Props (Farbe, Größe, Schriftgröße, Padding, Ausrichtung,
 * Kursiv, Unterstrichen, Icon) kommen aus dem Admin-Editor
 * `PromoBannerSettings`. Fallback-Defaults hier gespiegelt.
 */
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { PhoneCall, X, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'ke_promo_banner_dismissed_v1';

const DEFAULTS = {
  enabled: true,
  text: 'Noch Hüpfburgen zur Einschulung 2026 verfügbar!',
  cta_label: 'Auf telefonische Anfrage:',
  phone_display: '0174 681 4861',
  phone_tel: '+491746814861',
  bg_color: '#fdc20f',
  text_color: '#005898',
  dismissible: true,
  font_size_px: 14,
  padding_y_px: 8,
  padding_x_px: 16,
  font_weight: 700,
  font_style: 'normal',
  text_decoration: 'none',
  text_align: 'left',
  icon: 'sparkles',
};

const iconFor = (name) => {
  if (name === 'phone') return PhoneCall;
  if (name === 'none') return null;
  return Sparkles;
};

const PromoBanner = () => {
  const { settings } = useSettings();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  const cfg = useMemo(() => {
    const c = (settings && settings.promo_banner) || {};
    return { ...DEFAULTS, ...c };
  }, [settings]);

  const isPublic = useMemo(() => {
    const p = (location.pathname || '/').toLowerCase();
    return !p.startsWith('/admin') && !p.startsWith('/fahrer');
  }, [location.pathname]);

  if (!isPublic || !cfg.enabled || dismissed) return null;

  const onDismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const telHref = cfg.phone_tel || `tel:${(cfg.phone_display || '').replace(/[\s()\/]/g, '')}`;
  const IconCmp = iconFor(cfg.icon);

  const justify = cfg.text_align === 'center' ? 'justify-center'
    : cfg.text_align === 'right' ? 'justify-end'
    : 'justify-start';

  return (
    <div
      className="w-full relative overflow-hidden shadow-sm"
      style={{ backgroundColor: cfg.bg_color, color: cfg.text_color }}
      role="banner"
      data-testid="promo-banner"
    >
      <div
        className={`max-w-7xl mx-auto flex items-center gap-2 md:gap-4 ${justify}`}
        style={{
          paddingTop: `${cfg.padding_y_px}px`,
          paddingBottom: `${cfg.padding_y_px}px`,
          paddingLeft: `${cfg.padding_x_px}px`,
          paddingRight: `${cfg.padding_x_px}px`,
        }}
      >
        {IconCmp && cfg.text_align !== 'right' && (
          <IconCmp
            className="shrink-0"
            style={{ width: cfg.font_size_px + 2, height: cfg.font_size_px + 2 }}
            aria-hidden
          />
        )}
        <span
          className="leading-snug flex-1"
          style={{
            fontSize: `${cfg.font_size_px}px`,
            fontWeight: cfg.font_weight,
            fontStyle: cfg.font_style,
            textDecoration: cfg.text_decoration,
            textAlign: cfg.text_align,
          }}
          data-testid="promo-banner-text"
        >
          <span>{cfg.text}</span>
          {cfg.cta_label ? (
            <span className="hidden sm:inline"> · {cfg.cta_label}</span>
          ) : null}
        </span>
        {cfg.phone_display && (
          <a
            href={telHref.startsWith('tel:') ? telHref : `tel:${telHref}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-black/10 hover:bg-black/20 transition whitespace-nowrap"
            style={{
              color: cfg.text_color,
              fontSize: `${Math.max(11, cfg.font_size_px - 1)}px`,
              fontWeight: cfg.font_weight,
              padding: '4px 12px',
            }}
            data-testid="promo-banner-phone"
          >
            <PhoneCall className="h-3.5 w-3.5" aria-hidden />
            <span>{cfg.phone_display}</span>
          </a>
        )}
        {cfg.dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Hinweis schließen"
            data-testid="promo-banner-dismiss"
            className="p-1 rounded hover:bg-black/10 transition shrink-0"
            style={{ color: cfg.text_color }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PromoBanner;
